/**
 * hypervisor-migration.js — Hypervisor Migration Section
 *
 * Owns:
 *   - buildDefaultHypervisorMigrationEntry  (data model factory)
 *   - DOM refs for the hypervisor migration section
 *   - setHvmEnabled + toggle event binding
 *   - syncHypervisorMigrationSectionVisibility, renderHypervisorMigrationSection
 *   - All delegated DOM events for the HVM rows host and add-migration button
 *
 * VSA migration (moving Windows VBR servers to Linux) lives in vsa-migration.js —
 * it is conceptually distinct from hypervisor migration (migrating VM workloads).
 *
 * Exports:
 *   syncHypervisorMigrationSectionVisibility — called from syncDesignOptionFields in script.js
 *
 */

import { AppState, hypervisorMigrationScopes } from './state.js';
import {
    jobWorkloadOptions, jobTypeOptions, getJobTypeDef, getJobWorkloadDef, stripWorkloadUnitSuffix,
    getHypervisorBackupJobsFromSites, getHypervisorUnprotectedWorkloadsFromSites,
    pruneStaleHypervisorMigrationScopes,
} from './workload-defs.js';
import { escapeHtml } from './utils.js';
import { scheduleRenderEstimate, getGfChipIcon } from './recalc.js';
import {
    setCollapsibleSectionState, syncMigrationGroupVisibility,
    createHypervisorMigrationId,
} from './helpers.js';
import { renderScopeMatrix, syncMatrixToHiddenInputs } from './scope-matrix.js';
import { renderStep2Canvas } from './step2-canvas.js';
import { emit, BUS_EVENTS } from './bus.js';

// ── Hypervisor migration data model ──────────────────────────────────────────

export function buildDefaultHypervisorMigrationEntry(sourceRef = null, targetPlatform = 'hyperv', targetVmCount = 50) {
    return {
        id: createHypervisorMigrationId(),
        sourceRef,              // null | "job:<id>" | "uwl:<id>" — references a step-1 entity
        targetPlatform,         // workloadType value for the destination hypervisor
        targetVmCount,          // number of VMs being migrated to the target platform
        includeProxyReconfig: true,
        includeRestoreTesting: true,
        includeInScope: false
    };
}

// ── Hypervisor Migration Section ─────────────────────────────────────────────

const hypervisorMigrationSection = document.getElementById('design-hypervisor-migration-section');
window.hypervisorMigrationSection = hypervisorMigrationSection;
const hypervisorMigrationRowsHost = document.getElementById('hypervisor-migration-rows-host');
const hvmEnableToggleBtn = document.getElementById('hvm-section-enable-toggle');

/**
 * Enable or disable the hypervisor migration feature via the pill toggle.
 * Enabling expands the section; disabling collapses it and zeroes its effort.
 */
function setHvmEnabled(enabled) {
    AppState.hvmEnabled = Boolean(enabled);
    if (hypervisorMigrationSection) {
        hypervisorMigrationSection.dataset.hvmEnabled = String(AppState.hvmEnabled);
    }
    if (hvmEnableToggleBtn) {
        hvmEnableToggleBtn.setAttribute('aria-pressed', String(AppState.hvmEnabled));
    }
    if (!AppState.hvmEnabled) {
        setCollapsibleSectionState('hypervisor-migration', true);
    }
}

// Wire up the enable/disable toggle button
if (hvmEnableToggleBtn) {
    hvmEnableToggleBtn.addEventListener('click', () => {
        setHvmEnabled(!AppState.hvmEnabled);
        scheduleRenderEstimate();
    });
}

// (The hvm-section-enable-toggle button has been removed from the HTML;
//  the section is always expandable — effort is gated by whether scopes are configured.)

/** Show/hide the section based on whether any hypervisor-type entities exist in step 1
 *  (backup jobs OR unprotected workloads). Also prunes stale scope entries. */
export function syncHypervisorMigrationSectionVisibility() {
    const pruned = pruneStaleHypervisorMigrationScopes();
    const hypervisorJobs = getHypervisorBackupJobsFromSites();
    const hypervisorUwls = getHypervisorUnprotectedWorkloadsFromSites();
    const shouldShow = hypervisorJobs.length > 0 || hypervisorUwls.length > 0;
    if (hypervisorMigrationSection) {
        if (!shouldShow && hypervisorMigrationSection.contains(document.activeElement)) { document.activeElement.blur(); }
        hypervisorMigrationSection.hidden = !shouldShow;
        hypervisorMigrationSection.style.display = shouldShow ? '' : 'none';
        hypervisorMigrationSection.setAttribute('aria-hidden', String(!shouldShow));
    }
    if (pruned) {
        scheduleRenderEstimate();
    }
    renderHypervisorMigrationSection();
    syncMigrationGroupVisibility();
}

/** Renders all migration scope entry cards into the host div. */
function renderHypervisorMigrationSection() {
    if (!hypervisorMigrationRowsHost) return;
    const hypervisorJobs = getHypervisorBackupJobsFromSites();
    const hypervisorUwls = getHypervisorUnprotectedWorkloadsFromSites();
    const hypervisorTargetOptions = jobWorkloadOptions.filter((w) => w.group === 'Hypervisors');

    if (hypervisorMigrationScopes.length === 0) {
        hypervisorMigrationRowsHost.innerHTML = `<p class="hvm-empty-hint">No migrations defined. Use <em>Add migration</em> below to link a step 1 backup job or unprotected workload to its target platform.</p>`;
        return;
    }

    const svgArrow = `<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    const svgClose = `<svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;

    const rowsHtml = hypervisorMigrationScopes.map((scope) => {
        // ── Source select (grouped optgroups) ──────────────────────────────
        const jobOptsHtml = hypervisorJobs.map((j) => {
            const wlDef = getJobWorkloadDef(j.workloadType);
            const label = (j.customLabel || (wlDef ? wlDef.label : j.workloadType)) + ` (${j.count || 0} VMs)`;
            const ref   = `job:${j.id}`;
            const sel   = scope.sourceRef === ref ? ' selected' : '';
            return `<option value="${escapeHtml(ref)}"${sel}>${escapeHtml(label)}</option>`;
        }).join('');
        const uwlOptsHtml = hypervisorUwls.map((w) => {
            const wlDef = getJobWorkloadDef(w.workloadType);
            const label = (w.customLabel || (wlDef ? wlDef.label : w.workloadType)) + ` (${w.count || 0} VMs)`;
            const ref   = `uwl:${w.id}`;
            const sel   = scope.sourceRef === ref ? ' selected' : '';
            return `<option value="${escapeHtml(ref)}"${sel}>${escapeHtml(label)}</option>`;
        }).join('');
        let groupedOpts = '';
        if (hypervisorJobs.length > 0) groupedOpts += `<optgroup label="Existing backup jobs">${jobOptsHtml}</optgroup>`;
        if (hypervisorUwls.length > 0)  groupedOpts += `<optgroup label="Unprotected workloads">${uwlOptsHtml}</optgroup>`;

        // ── Source chip preview ────────────────────────────────────────────
        let sourceChipHtml = '';
        if (scope.sourceRef) {
            if (scope.sourceRef.startsWith('job:')) {
                const jobId     = parseInt(scope.sourceRef.slice(4), 10);
                const sourceJob = hypervisorJobs.find((j) => j.id === jobId) || null;
                if (sourceJob) {
                    const wlDef   = getJobWorkloadDef(sourceJob.workloadType);
                    const wlLabel = sourceJob.customLabel || (wlDef ? stripWorkloadUnitSuffix(wlDef.label, wlDef.unit) : sourceJob.workloadType);
                    const wlUnit  = wlDef ? wlDef.unit : 'VMs';
                    const wlIcon  = getGfChipIcon(sourceJob.workloadType) || '';
                    const typeDef = getJobTypeDef(sourceJob.jobType) || jobTypeOptions[0];
                    sourceChipHtml = `<span class="gf-chip gf-chip--job hvm-source-chip" title="Source backup job from step 1">
                        <span class="gf-chip-row-name">
                            <span class="gf-job-type-badge" style="pointer-events:none">${escapeHtml(typeDef.label)}</span>
                            ${wlIcon ? `<span class="gf-chip-icon" style="pointer-events:none">${wlIcon}</span>` : ''}
                            <span class="gf-chip-label" style="pointer-events:none">${escapeHtml(wlLabel)}</span>
                            <span class="hvm-source-badge">migration source</span>
                        </span>
                        <span class="gf-chip-row-qty">
                            <span class="gf-chip-x-symbol" aria-hidden="true">×</span>
                            <span class="gf-chip-qty" style="pointer-events:none;padding:0 2px">${sourceJob.count || 0}</span>
                            <span class="gf-chip-unit">${escapeHtml(wlUnit)}</span>
                        </span>
                    </span>`;
                }
            } else if (scope.sourceRef.startsWith('uwl:')) {
                const uwlId  = parseInt(scope.sourceRef.slice(4), 10);
                const srcUwl = hypervisorUwls.find((w) => w.id === uwlId) || null;
                if (srcUwl) {
                    const wlDef   = getJobWorkloadDef(srcUwl.workloadType);
                    const wlLabel = srcUwl.customLabel || (wlDef ? stripWorkloadUnitSuffix(wlDef.label, wlDef.unit) : srcUwl.workloadType);
                    const wlUnit  = wlDef ? wlDef.unit : 'VMs';
                    const wlIcon  = getGfChipIcon(srcUwl.workloadType) || '';
                    sourceChipHtml = `<span class="gf-chip hvm-source-chip" title="Unprotected workload from step 1">
                        <span class="gf-chip-row-name">
                            <span class="gf-job-type-badge hvm-uwl-badge" style="pointer-events:none">Unprotected</span>
                            ${wlIcon ? `<span class="gf-chip-icon" style="pointer-events:none">${wlIcon}</span>` : ''}
                            <span class="gf-chip-label" style="pointer-events:none">${escapeHtml(wlLabel)}</span>
                            <span class="hvm-source-badge">migration source</span>
                        </span>
                        <span class="gf-chip-row-qty">
                            <span class="gf-chip-x-symbol" aria-hidden="true">×</span>
                            <span class="gf-chip-qty" style="pointer-events:none;padding:0 2px">${srcUwl.count || 0}</span>
                            <span class="gf-chip-unit">${escapeHtml(wlUnit)}</span>
                        </span>
                    </span>`;
                }
            }
        }

        // ── Target select ──────────────────────────────────────────────────
        const targetOpts = hypervisorTargetOptions.map((w) => {
            const sel = scope.targetPlatform === w.value ? ' selected' : '';
            return `<option value="${escapeHtml(w.value)}"${sel}>${escapeHtml(w.label)}</option>`;
        }).join('');

        return `<tr class="hvm-row" data-hvm-scope="${scope.id}">
            <td class="hvm-td hvm-td--from">
                <select class="hvm-select" data-hvm-source-select="${scope.id}" aria-label="Source platform">
                    <option value="">— select source —</option>
                    ${groupedOpts}
                </select>
                ${sourceChipHtml}
            </td>
            <td class="hvm-td hvm-td--arrow" aria-hidden="true">${svgArrow}</td>
            <td class="hvm-td hvm-td--to">
                <select class="hvm-select" data-hvm-target-select="${scope.id}" aria-label="Target platform">
                    ${targetOpts}
                </select>
                <div class="hvm-vm-count-row">
                    <label for="hvm-count-${scope.id}" class="hvm-vm-count-label">VMs</label>
                    <input id="hvm-count-${scope.id}" type="number" class="hvm-vm-count-input"
                           min="0" step="1" value="${scope.targetVmCount}"
                           data-hvm-count="${scope.id}" aria-label="Target VM count">
                </div>
            </td>
            <td class="hvm-td hvm-td--opts">
                <label class="hvm-opt-label">
                    <input type="checkbox" data-hvm-proxy-reconfig="${scope.id}"${scope.includeProxyReconfig ? ' checked' : ''}>
                    Proxy reconfiguration
                </label>
                <label class="hvm-opt-label">
                    <input type="checkbox" data-hvm-restore-testing="${scope.id}"${scope.includeRestoreTesting ? ' checked' : ''}>
                    Restore testing
                </label>
                <label class="hvm-opt-label hvm-opt-label--scope" title="Adds the target platform and VM count to the workload scope matrix so you can configure protection and operations for the migrated workloads">
                    <input type="checkbox" data-hvm-include-scope="${scope.id}"${scope.includeInScope ? ' checked' : ''}>
                    Add to workload scope matrix
                </label>
            </td>
            <td class="hvm-td hvm-td--remove">
                <button type="button" class="hvm-remove-btn" data-hvm-remove="${scope.id}" aria-label="Remove migration scope">${svgClose}</button>
            </td>
        </tr>`;
    }).join('');

    hypervisorMigrationRowsHost.innerHTML = `
        <div class="hvm-table-wrap">
            <table class="hvm-table">
                <thead>
                    <tr>
                        <th class="hvm-th hvm-th--from">Source platform</th>
                        <th class="hvm-th hvm-th--arrow"></th>
                        <th class="hvm-th hvm-th--to">Target platform</th>
                        <th class="hvm-th hvm-th--opts">Options</th>
                        <th class="hvm-th hvm-th--remove"></th>
                    </tr>
                </thead>
                <tbody>${rowsHtml}</tbody>
            </table>
        </div>`;
}

// ── Hypervisor Migration Section Events ──────────────────────────────────────

// Add migration scope
document.getElementById('add-hypervisor-migration-btn')?.addEventListener('click', () => {
    // Pre-select the first available hypervisor source (backup job preferred, then unprotected workload)
    const availableJobs = getHypervisorBackupJobsFromSites();
    const availableUwls = getHypervisorUnprotectedWorkloadsFromSites();
    let defaultSourceRef = null;
    let sourceWorkload   = null;
    let defaultCount     = 50;
    if (availableJobs.length > 0) {
        defaultSourceRef = `job:${availableJobs[0].id}`;
        sourceWorkload   = availableJobs[0].workloadType;
        defaultCount     = availableJobs[0].count || 50;
    } else if (availableUwls.length > 0) {
        defaultSourceRef = `uwl:${availableUwls[0].id}`;
        sourceWorkload   = availableUwls[0].workloadType;
        defaultCount     = availableUwls[0].count || 50;
    }
    // Pre-select the first hypervisor target that differs from the source platform
    const targetOptions = jobWorkloadOptions.filter((w) => w.group === 'Hypervisors');
    const defaultTarget = targetOptions.find((w) => w.value !== sourceWorkload)?.value
        || targetOptions[0]?.value
        || 'hyperv';
    hypervisorMigrationScopes.push(buildDefaultHypervisorMigrationEntry(defaultSourceRef, defaultTarget, defaultCount));
    renderHypervisorMigrationSection();
    scheduleRenderEstimate();
});

// Delegated events for the migration rows host (remove, selects, inputs, checkboxes)
document.getElementById('hypervisor-migration-rows-host')?.addEventListener('click', (e) => {
    const removeBtn = e.target.closest('[data-hvm-remove]');
    if (removeBtn) {
        const scopeId = parseInt(removeBtn.dataset.hvmRemove, 10);
        const idx = hypervisorMigrationScopes.findIndex((s) => s.id === scopeId);
        if (idx !== -1) {
            hypervisorMigrationScopes.splice(idx, 1);
            renderHypervisorMigrationSection();
            scheduleRenderEstimate();
            emit(BUS_EVENTS.SITE_LIST_CHANGED); // refresh step 1 badges
        }
    }
});

document.getElementById('hypervisor-migration-rows-host')?.addEventListener('change', (e) => {
    // Source selector (job or unprotected workload)
    const sourceSelect = e.target.closest('[data-hvm-source-select]');
    if (sourceSelect) {
        const scopeId = parseInt(sourceSelect.dataset.hvmSourceSelect, 10);
        const scope = hypervisorMigrationScopes.find((s) => s.id === scopeId);
        if (scope) {
            scope.sourceRef = sourceSelect.value || null;  // "job:<id>" | "uwl:<id>" | null
            scheduleRenderEstimate();
            renderHypervisorMigrationSection(); // refresh source chip
        }
        return;
    }

    // Target platform selector
    const targetSelect = e.target.closest('[data-hvm-target-select]');
    if (targetSelect) {
        const scopeId = parseInt(targetSelect.dataset.hvmTargetSelect, 10);
        const scope = hypervisorMigrationScopes.find((s) => s.id === scopeId);
        if (scope) {
            scope.targetPlatform = targetSelect.value;
            scheduleRenderEstimate();
        }
        return;
    }

    // Proxy reconfig checkbox
    const proxyCheck = e.target.closest('[data-hvm-proxy-reconfig]');
    if (proxyCheck) {
        const scopeId = parseInt(proxyCheck.dataset.hvmProxyReconfig, 10);
        const scope = hypervisorMigrationScopes.find((s) => s.id === scopeId);
        if (scope) {
            scope.includeProxyReconfig = proxyCheck.checked;
            scheduleRenderEstimate();
        }
        return;
    }

    // Restore testing checkbox
    const restoreCheck = e.target.closest('[data-hvm-restore-testing]');
    if (restoreCheck) {
        const scopeId = parseInt(restoreCheck.dataset.hvmRestoreTesting, 10);
        const scope = hypervisorMigrationScopes.find((s) => s.id === scopeId);
        if (scope) {
            scope.includeRestoreTesting = restoreCheck.checked;
            scheduleRenderEstimate();
        }
        return;
    }

    // Add to workload scope matrix checkbox
    const scopeCheck = e.target.closest('[data-hvm-include-scope]');
    if (scopeCheck) {
        const scopeId = parseInt(scopeCheck.dataset.hvmIncludeScope, 10);
        const scope = hypervisorMigrationScopes.find((s) => s.id === scopeId);
        if (scope) {
            scope.includeInScope = scopeCheck.checked;
            renderScopeMatrix();       // immediately refresh the workload scope matrix
            renderStep2Canvas();       // add/remove the target workload chip from VBR placement
            syncMatrixToHiddenInputs();
            scheduleRenderEstimate();
        }
        return;
    }
});

document.getElementById('hypervisor-migration-rows-host')?.addEventListener('input', (e) => {
    // VM count input
    const countInput = e.target.closest('[data-hvm-count]');
    if (countInput) {
        const scopeId = parseInt(countInput.dataset.hvmCount, 10);
        const scope = hypervisorMigrationScopes.find((s) => s.id === scopeId);
        if (scope) {
            scope.targetVmCount = Math.max(0, parseInt(countInput.value, 10) || 0);
            scheduleRenderEstimate();
        }
    }
});

// ── End Hypervisor Migration Section ─────────────────────────────────────────
